import { ChevronDown, ChevronRight, Star } from 'lucide-react';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';

export interface Option {
  key: string;
  value: string;
}

interface OptionGroup {
  key: string;
  label: string;
  options: Option[];
  defaultOpen?: boolean;
}

interface MultiSelectCheckBoxProps
  extends Omit<React.InputHTMLAttributes<HTMLSelectElement>, 'onChange'> {
  id: string;
  name: string;
  options?: Option[];
  optionGroups?: OptionGroup[];
  selectedOptions: string[];
  onChange: (_selected: string[]) => void;
  label?: string;
  placeholder?: string;
  textClass?: string;
  dropdownMaxHeightClass?: string;

  /** Optional controlled open state: if provided the component becomes controlled for open/close */
  isOpen?: boolean;
  setIsOpen?: (_open: boolean) => void;
  isOptionDisabled?: (_key: string) => boolean;
  getOptionTooltip?: (_key: string) => string | undefined;
  renderOptionLabel?: (_option: Option) => ReactNode;
  searchable?: boolean;
  searchPlaceholder?: string;
  selectAllMode?: 'default' | 'clear-on-partial';
  sortSelectedFirst?: boolean;
  maxSelectedOptions?: number;
  /** When true, render a star toggle next to each option and pin favorites at the top of the list. Only affects the non-grouped (flat) list. */
  favoriteEnabled?: boolean;
  isFavorite?: (_key: string) => boolean;
  onToggleFavorite?: (_key: string) => void;
  favoriteSectionLabel?: string;
}

const INITIAL_VISIBLE_OPTIONS_COUNT = 200;

const MultiSelectCheckBox = ({
  id,
  name,
  options = [],
  optionGroups,
  selectedOptions,
  onChange,
  label,
  placeholder = 'Please select',
  textClass = '',
  dropdownMaxHeightClass = 'max-h-80',
  isOpen: controlledIsOpen,
  setIsOpen: controlledSetIsOpen,
  isOptionDisabled,
  getOptionTooltip,
  renderOptionLabel,
  searchable = false,
  searchPlaceholder = 'Search...',
  selectAllMode = 'default',
  sortSelectedFirst = false,
  maxSelectedOptions,
  favoriteEnabled = false,
  isFavorite,
  onToggleFavorite,
  favoriteSectionLabel = 'Favorites',
  ...props
}: MultiSelectCheckBoxProps) => {
  // internal state used when parent doesn't control open state
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledIsOpen ?? internalOpen;
  const setOpen = (v: boolean) => {
    if (typeof controlledSetIsOpen === 'function') {
      controlledSetIsOpen(v);
    } else {
      setInternalOpen(v);
    }
  };

  const [dropdownPosition, setDropdownPosition] = useState<'bottom' | 'top'>(
    'bottom'
  );
  const [portalReady, setPortalReady] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState<{
    top: number;
    left: number;
    width: number;
    maxHeight: number;
  } | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(
    {}
  );
  const [visibleOptionsCount, setVisibleOptionsCount] = useState(
    INITIAL_VISIBLE_OPTIONS_COUNT
  );
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLUListElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const selectAllCheckboxRef = useRef<HTMLInputElement>(null);
  const normalizedSearchTerm = searchTerm.trim().toLowerCase();
  const isGrouped = Boolean(optionGroups?.length);
  const allOptions = useMemo(
    () =>
      isGrouped
        ? (optionGroups ?? []).flatMap((group) => group.options)
        : options,
    [isGrouped, optionGroups, options]
  );
  const selectedOptionSet = useMemo(
    () => new Set(selectedOptions),
    [selectedOptions]
  );
  const isSelectionLimitReached =
    typeof maxSelectedOptions === 'number' &&
    selectedOptions.length >= maxSelectedOptions;
  const isOptionExternallyDisabled = useCallback(
    (key: string) => isOptionDisabled?.(key) ?? false,
    [isOptionDisabled]
  );
  const isOptionSelectionDisabled = useCallback(
    (key: string) =>
      isOptionExternallyDisabled(key) ||
      (isSelectionLimitReached && !selectedOptionSet.has(key)),
    [isOptionExternallyDisabled, isSelectionLimitReached, selectedOptionSet]
  );
  const optionLabelByKey = useMemo(
    () => new Map(allOptions.map((item) => [item.key, item.value])),
    [allOptions]
  );
  const optionOrderByKey = useMemo(
    () => new Map(allOptions.map((item, index) => [item.key, index])),
    [allOptions]
  );
  const enabledOptionKeys = useMemo(
    () =>
      allOptions
        .filter((item) => !isOptionExternallyDisabled(item.key))
        .map((item) => item.key),
    [allOptions, isOptionExternallyDisabled]
  );
  const enabledSelectedCount = useMemo(
    () => enabledOptionKeys.filter((key) => selectedOptionSet.has(key)).length,
    [enabledOptionKeys, selectedOptionSet]
  );
  const selectAllTargetKeys = useMemo(() => {
    if (typeof maxSelectedOptions !== 'number') {
      return enabledOptionKeys;
    }

    if (!favoriteEnabled || !isFavorite) {
      return enabledOptionKeys.slice(0, maxSelectedOptions);
    }

    const favorites: string[] = [];
    const nonFavorites: string[] = [];
    enabledOptionKeys.forEach((key) => {
      if (isFavorite(key)) {
        favorites.push(key);
      } else {
        nonFavorites.push(key);
      }
    });
    return [...favorites, ...nonFavorites].slice(0, maxSelectedOptions);
  }, [enabledOptionKeys, maxSelectedOptions, favoriteEnabled, isFavorite]);
  const selectAllTargetSelectedCount = useMemo(
    () =>
      selectAllTargetKeys.filter((key) => selectedOptionSet.has(key)).length,
    [selectAllTargetKeys, selectedOptionSet]
  );
  const isSelectAllChecked =
    selectAllTargetKeys.length > 0 &&
    selectAllTargetSelectedCount === selectAllTargetKeys.length;
  const isSelectAllPartiallyChecked =
    enabledSelectedCount > 0 && !isSelectAllChecked;
  const isSelectAllDisabled = enabledOptionKeys.length === 0;

  const applySelectionLimit = useCallback(
    (nextSelected: string[]) => {
      const uniqueSelected = Array.from(new Set(nextSelected));

      if (typeof maxSelectedOptions !== 'number') {
        return uniqueSelected;
      }

      const favoriteCheck =
        favoriteEnabled && isFavorite ? isFavorite : undefined;

      return uniqueSelected
        .sort((left, right) => {
          if (favoriteCheck) {
            const leftFavorite = favoriteCheck(left) ? 0 : 1;
            const rightFavorite = favoriteCheck(right) ? 0 : 1;
            if (leftFavorite !== rightFavorite) {
              return leftFavorite - rightFavorite;
            }
          }

          const leftRank =
            optionOrderByKey.get(left) ?? Number.MAX_SAFE_INTEGER;
          const rightRank =
            optionOrderByKey.get(right) ?? Number.MAX_SAFE_INTEGER;

          if (leftRank !== rightRank) return leftRank - rightRank;
          return left.localeCompare(right);
        })
        .slice(0, maxSelectedOptions);
    },
    [maxSelectedOptions, optionOrderByKey, favoriteEnabled, isFavorite]
  );

  const handleOnChange = (key: string) => {
    const isExist = selectedOptionSet.has(key);
    if (isExist) {
      onChange(selectedOptions.filter((i) => i !== key));
    } else {
      onChange(applySelectionLimit([...selectedOptions, key]));
    }
  };

  const handleSelectAll = () => {
    const allSelected = isSelectAllChecked;
    const partiallySelected = isSelectAllPartiallyChecked;

    if (
      allSelected ||
      (selectAllMode === 'clear-on-partial' && partiallySelected)
    ) {
      const enabledOptionKeySet = new Set(enabledOptionKeys);
      onChange(selectedOptions.filter((key) => !enabledOptionKeySet.has(key)));
      return;
    }

    onChange(applySelectionLimit([...selectedOptions, ...enabledOptionKeys]));
  };

  const optionGroupByKey = useMemo(
    () => new Map((optionGroups ?? []).map((group) => [group.key, group])),
    [optionGroups]
  );

  const sortOptions = useCallback(
    (items: Option[]) => {
      if (!sortSelectedFirst) {
        return items;
      }

      return [...items].sort((left, right) => {
        const leftSelected = selectedOptionSet.has(left.key);
        const rightSelected = selectedOptionSet.has(right.key);

        if (leftSelected !== rightSelected) {
          return leftSelected ? -1 : 1;
        }

        return left.value.localeCompare(right.value);
      });
    },
    [selectedOptionSet, sortSelectedFirst]
  );

  const filteredOptions = useMemo(() => {
    if (isGrouped) {
      return [];
    }

    const matchedOptions = options.filter((item) =>
      item.value.toLowerCase().includes(normalizedSearchTerm)
    );

    return sortOptions(matchedOptions);
  }, [isGrouped, normalizedSearchTerm, options, sortOptions]);

  const filteredOptionGroups = useMemo(
    () =>
      (optionGroups ?? [])
        .map((group) => ({
          ...group,
          options: sortOptions(
            group.options.filter((item) =>
              item.value.toLowerCase().includes(normalizedSearchTerm)
            )
          ),
        }))
        .filter((group) => group.options.length > 0),
    [normalizedSearchTerm, optionGroups, sortOptions]
  );

  const { favoriteFilteredOptions, nonFavoriteFilteredOptions } =
    useMemo(() => {
      if (!favoriteEnabled || isGrouped) {
        return {
          favoriteFilteredOptions: [] as Option[],
          nonFavoriteFilteredOptions: filteredOptions,
        };
      }
      const favs: Option[] = [];
      const others: Option[] = [];
      filteredOptions.forEach((item) => {
        if (isFavorite?.(item.key)) {
          favs.push(item);
        } else {
          others.push(item);
        }
      });
      return {
        favoriteFilteredOptions: favs,
        nonFavoriteFilteredOptions: others,
      };
    }, [favoriteEnabled, filteredOptions, isFavorite, isGrouped]);

  const visibleNonFavoriteOptions = useMemo(
    () => nonFavoriteFilteredOptions.slice(0, visibleOptionsCount),
    [nonFavoriteFilteredOptions, visibleOptionsCount]
  );

  const hasMoreOptions =
    !isGrouped &&
    visibleNonFavoriteOptions.length < nonFavoriteFilteredOptions.length;

  const getEnabledGroupOptionKeys = (group: OptionGroup) =>
    group.options
      .filter((item) => !isOptionExternallyDisabled(item.key))
      .map((item) => item.key);

  const getGroupSelectionState = (group: OptionGroup) => {
    const groupOptionKeys = getEnabledGroupOptionKeys(group);
    const selectedCount = groupOptionKeys.filter((key) =>
      selectedOptionSet.has(key)
    ).length;

    return {
      selectedCount,
      totalCount: groupOptionKeys.length,
      checked:
        groupOptionKeys.length > 0 && selectedCount === groupOptionKeys.length,
      indeterminate:
        selectedCount > 0 && selectedCount < groupOptionKeys.length,
      disabled: groupOptionKeys.length === 0,
    };
  };

  const handleGroupSelect = (group: OptionGroup) => {
    const groupOptionKeys = getEnabledGroupOptionKeys(group);
    const selectedCount = groupOptionKeys.filter((key) =>
      selectedOptionSet.has(key)
    ).length;
    const allSelected =
      groupOptionKeys.length > 0 && selectedCount === groupOptionKeys.length;
    const partiallySelected =
      selectedCount > 0 && selectedCount < groupOptionKeys.length;

    if (
      allSelected ||
      (selectAllMode === 'clear-on-partial' && partiallySelected)
    ) {
      const groupOptionKeySet = new Set(groupOptionKeys);
      onChange(selectedOptions.filter((key) => !groupOptionKeySet.has(key)));
      return;
    }

    onChange(applySelectionLimit([...selectedOptions, ...groupOptionKeys]));
  };

  const optionGroupKeys = useMemo(
    () => (optionGroups ?? []).map((group) => group.key).join('|'),
    [optionGroups]
  );

  useEffect(() => {
    if (!optionGroups?.length) return;

    setExpandedGroups((previous) => {
      const next = optionGroups.reduce<Record<string, boolean>>(
        (acc, group) => {
          acc[group.key] = previous[group.key] ?? group.defaultOpen ?? true;
          return acc;
        },
        {}
      );
      const previousKeys = Object.keys(previous);
      const changed =
        previousKeys.length !== optionGroups.length ||
        optionGroups.some((group) => previous[group.key] !== next[group.key]);

      return changed ? next : previous;
    });
  }, [optionGroupKeys, optionGroups]);

  useEffect(() => {
    setPortalReady(true);
  }, []);

  const updateDropdownPosition = useCallback(() => {
    if (!buttonRef.current) return;

    const buttonRect = buttonRef.current.getBoundingClientRect();
    const dropdownEl = dropdownRef.current;
    const viewportHeight = window.innerHeight;

    const topBarOffset = 114; // header height
    const spaceBelow = viewportHeight - buttonRect.bottom - 16;
    const spaceAbove = buttonRect.top - topBarOffset - 16; // subtract the header area

    const dropdownHeight = dropdownEl?.scrollHeight ?? 0;

    const nextPosition: 'top' | 'bottom' =
      spaceBelow < dropdownHeight && spaceAbove > spaceBelow ? 'top' : 'bottom';
    const maxHeight = Math.max(
      nextPosition === 'top' ? spaceAbove : spaceBelow,
      0
    );

    setDropdownPosition(nextPosition);
    setDropdownStyle({
      top:
        nextPosition === 'top'
          ? buttonRect.top - 12 // equivalent to previous mb-3 gap
          : buttonRect.bottom + 12, // equivalent to previous mt-3 gap
      left: buttonRect.left,
      width: buttonRect.width,
      maxHeight,
    });
  }, []);

  // recompute on open, and whenever content that can change the dropdown's
  // scrollHeight (search filtering, "show more") could shift the flip/maxHeight decision
  useLayoutEffect(() => {
    if (!open) return;
    updateDropdownPosition();
  }, [
    open,
    updateDropdownPosition,
    normalizedSearchTerm,
    visibleOptionsCount,
    filteredOptions.length,
    filteredOptionGroups.length,
  ]);

  // portaled dropdown no longer moves with ancestor scroll, so track the trigger explicitly
  useEffect(() => {
    if (!open) return;

    const handleViewportChange = () => updateDropdownPosition();

    window.addEventListener('scroll', handleViewportChange, true);
    window.addEventListener('resize', handleViewportChange);

    return () => {
      window.removeEventListener('scroll', handleViewportChange, true);
      window.removeEventListener('resize', handleViewportChange);
    };
  }, [open, updateDropdownPosition]);

  useEffect(() => {
    if (!open) {
      setSearchTerm('');
      setVisibleOptionsCount(INITIAL_VISIBLE_OPTIONS_COUNT);
      return;
    }

    if (searchable) {
      searchInputRef.current?.focus();
    }
  }, [open, searchable]);

  useEffect(() => {
    setVisibleOptionsCount(INITIAL_VISIBLE_OPTIONS_COUNT);
  }, [normalizedSearchTerm]);

  useEffect(() => {
    if (!selectAllCheckboxRef.current) return;

    selectAllCheckboxRef.current.indeterminate = isSelectAllPartiallyChecked;
  }, [isSelectAllPartiallyChecked]);

  // Close on Escape key (only when open)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const convertStartToUppercase = (text: string) =>
    `${text.charAt(0).toUpperCase()}${text.slice(1)}`;

  const selectedLabels = useMemo(
    () => selectedOptions.map((key) => optionLabelByKey.get(key) ?? key),
    [optionLabelByKey, selectedOptions]
  );
  const selectedLabelOptions = useMemo(
    () =>
      selectedOptions.map((key) => ({
        key,
        value: optionLabelByKey.get(key) ?? key,
      })),
    [optionLabelByKey, selectedOptions]
  );
  const renderSelectedLabel = () => {
    if (selectedOptions.length === 0) {
      return convertStartToUppercase(placeholder);
    }

    if (selectedOptions.length > 2 || !renderOptionLabel) {
      return selectedOptions.length > 2
        ? `${selectedOptions.length} ${name} selected`
        : selectedLabels.join(', ');
    }

    return (
      <span className="inline-flex max-w-full flex-wrap items-center gap-1">
        {selectedLabelOptions.map((option, index) => (
          <span key={option.key} className="inline-flex items-center gap-1">
            {renderOptionLabel(option)}
            {index < selectedLabelOptions.length - 1 ? ',' : null}
          </span>
        ))}
      </span>
    );
  };

  const renderOptionItem = (item: Option, nested = false) => {
    const selected = selectedOptionSet.has(item.key);
    const optionDisabled = isOptionSelectionDisabled(item.key);
    const tooltip =
      optionDisabled && !selected ? getOptionTooltip?.(item.key) : undefined;

    if (nested) {
      return (
        <li key={item.key} className="w-full">
          <label
            htmlFor={`${id}-${item.key}`}
            className={`flex cursor-pointer items-center gap-3 text-sm text-gray-700 ${
              optionDisabled && !selected ? 'cursor-not-allowed opacity-70' : ''
            }`}
            title={tooltip}
          >
            <input
              type="checkbox"
              id={`${id}-${item.key}`}
              className="rounded"
              checked={selected}
              disabled={optionDisabled && !selected}
              onChange={() => {
                if (optionDisabled && !selected) return;
                handleOnChange(item.key);
              }}
            />
            <span className={textClass}>
              {renderOptionLabel ? renderOptionLabel(item) : item.value}
            </span>
          </label>
        </li>
      );
    }

    const favorite = favoriteEnabled ? Boolean(isFavorite?.(item.key)) : false;

    return (
      <li
        key={item.key}
        className={`relative flex w-full items-center gap-x-2 rounded-lg my-0.5 py-0.5 ${
          optionDisabled && !selected
            ? 'opacity-70 cursor-not-allowed'
            : 'hover:bg-slate-50'
        }`}
        title={tooltip}
      >
        <label
          htmlFor={`${id}-${item.key}`}
          className={`flex gap-x-3 items-center w-full h-full py-2 ${
            favoriteEnabled ? 'pr-10' : 'pr-4'
          } ${nested ? 'pl-10' : 'pl-4'} ${
            optionDisabled && !selected
              ? 'cursor-not-allowed'
              : 'cursor-pointer'
          }`}
          title={tooltip}
        >
          <input
            type="checkbox"
            id={`${id}-${item.key}`}
            className="shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            checked={selected}
            disabled={optionDisabled && !selected}
            onChange={() => {
              if (optionDisabled && !selected) return;
              handleOnChange(item.key);
            }}
          />
          <span className={`text-sm flex items-center${textClass}`}>
            {renderOptionLabel ? renderOptionLabel(item) : item.value}
          </span>
        </label>
        {favoriteEnabled && (
          <button
            type="button"
            aria-label={
              favorite ? `Unfavorite ${item.value}` : `Favorite ${item.value}`
            }
            aria-pressed={favorite}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onToggleFavorite?.(item.key);
            }}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-slate-100 text-gray-400"
          >
            <Star
              className={`h-4 w-4 ${favorite ? 'fill-yellow-400 stroke-yellow-500' : 'stroke-current'}`}
            />
          </button>
        )}
      </li>
    );
  };

  return (
    <div className="relative">
      {label && (
        <label htmlFor={id} className="block text-sm font-medium mb-2">
          {convertStartToUppercase(label)}
        </label>
      )}

      {/* Trigger button */}
      <button
        disabled={props.disabled}
        ref={buttonRef}
        type="button"
        onClick={() => setOpen(!open)}
        className={`disabled:bg-gray-100 relative inline-flex justify-between items-center w-full py-[14px] ps-4 pe-2 bg-white border border-gray-200 rounded-lg text-gray-500 cursor-pointer ${open ? 'border-slate-500 z-20' : ''}`}
      >
        <span
          className={`text-sm ${textClass} block truncate max-w-[calc(100%-24px)]`}
          title={
            selectedOptions.length === 0
              ? convertStartToUppercase(placeholder)
              : selectedOptions.length > 2
                ? `${selectedOptions.length} ${name} selected`
                : selectedLabels.join(', ')
          }
        >
          {renderSelectedLabel()}
        </span>
        <ChevronDown className="shrink-0 ml-2" />
      </button>

      {/* Click-outside backdrop */}
      {open && (
        <button
          type="button"
          onClick={() => setOpen(false)}
          tabIndex={-1}
          className="w-screen h-full fixed inset-0 z-10"
        />
      )}

      {/* Dropdown */}
      {open &&
        portalReady &&
        createPortal(
          <ul
            ref={dropdownRef}
            className={`shadow-md ${dropdownMaxHeightClass} overflow-y-auto fixed z-[60] flex flex-col rounded-lg bg-white border border-gray-200 text-gray-800 ${
              isGrouped ? '' : 'px-1'
            }`}
            style={{
              top: dropdownStyle?.top ?? 0,
              left: dropdownStyle?.left ?? 0,
              width: dropdownStyle?.width,
              maxHeight: dropdownStyle?.maxHeight,
              transform:
                dropdownPosition === 'top' ? 'translateY(-100%)' : undefined,
              visibility: dropdownStyle ? 'visible' : 'hidden',
            }}
          >
            {searchable && (
              <li
                className={`sticky top-0 z-10 bg-white ${
                  isGrouped
                    ? 'border-b border-gray-100 px-4 py-3'
                    : 'border-b border-slate-100 px-3 py-2'
                }`}
              >
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  onClick={(event) => event.stopPropagation()}
                  placeholder={searchPlaceholder}
                  className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-700 outline-none focus:border-slate-400"
                />
              </li>
            )}

            {/* Select all */}
            <li
              className={
                isGrouped
                  ? 'border-b border-gray-100 px-4 py-3'
                  : `sticky inline-flex items-center gap-x-2 hover:bg-slate-50 border-b border-slate-100 bg-white z-[1] py-1 ${
                      searchable ? 'top-[53px]' : 'top-0'
                    }`
              }
            >
              <label
                htmlFor={`select-all-checkbox-${id}`}
                className={
                  isGrouped
                    ? 'flex cursor-pointer items-center gap-3 text-sm font-medium text-gray-700'
                    : 'flex gap-x-3 items-center w-full h-full py-2 px-4 cursor-pointer'
                }
              >
                <input
                  ref={selectAllCheckboxRef}
                  type="checkbox"
                  id={`select-all-checkbox-${id}`}
                  className={
                    isGrouped
                      ? 'rounded'
                      : 'shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-500'
                  }
                  checked={isSelectAllChecked}
                  disabled={isSelectAllDisabled}
                  onClick={handleSelectAll}
                  readOnly
                />
                <div className={isGrouped ? textClass : `text-sm ${textClass}`}>
                  {isGrouped ? 'All' : 'Select all'}
                </div>
              </label>
            </li>

            {/* Items */}
            {isGrouped ? (
              filteredOptionGroups.map((group) => {
                const sourceGroup = optionGroupByKey.get(group.key) ?? group;
                const selectionState = getGroupSelectionState(sourceGroup);
                const groupExpanded =
                  Boolean(normalizedSearchTerm) ||
                  (expandedGroups[group.key] ?? group.defaultOpen ?? true);

                return (
                  <li
                    key={group.key}
                    className="border-b border-gray-100 last:border-b-0"
                  >
                    <div className="flex items-center justify-between px-4 py-3">
                      <div className="flex min-w-0 items-center gap-2">
                        <button
                          type="button"
                          aria-label={
                            groupExpanded
                              ? `Collapse ${group.label}`
                              : `Expand ${group.label}`
                          }
                          onClick={() =>
                            setExpandedGroups((previous) => ({
                              ...previous,
                              [group.key]: !groupExpanded,
                            }))
                          }
                          className="rounded p-1 text-gray-500 hover:bg-gray-100"
                        >
                          {groupExpanded ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </button>
                        <label
                          htmlFor={`${id}-group-${group.key}`}
                          className="flex min-w-0 cursor-pointer items-center gap-3 text-sm font-medium text-gray-800"
                        >
                          <input
                            ref={(element) => {
                              if (element) {
                                element.indeterminate =
                                  selectionState.indeterminate;
                              }
                            }}
                            type="checkbox"
                            id={`${id}-group-${group.key}`}
                            className="rounded"
                            checked={selectionState.checked}
                            disabled={selectionState.disabled}
                            onChange={() => handleGroupSelect(sourceGroup)}
                          />
                          <span className={`truncate ${textClass}`}>
                            {group.label}
                          </span>
                        </label>
                      </div>
                    </div>
                    {groupExpanded && (
                      <div className="pb-2 pl-14 pr-4">
                        <ul className="flex flex-col gap-2">
                          {group.options.map((item) =>
                            renderOptionItem(item, true)
                          )}
                        </ul>
                      </div>
                    )}
                  </li>
                );
              })
            ) : (
              <>
                {favoriteEnabled && favoriteFilteredOptions.length > 0 && (
                  <>
                    <li className="sticky z-[1] px-4 pt-3 pb-1 text-xs font-semibold uppercase tracking-wide text-gray-500 bg-white">
                      {favoriteSectionLabel}
                    </li>
                    {favoriteFilteredOptions.map((item) =>
                      renderOptionItem(item)
                    )}
                    {nonFavoriteFilteredOptions.length > 0 && (
                      <li
                        aria-hidden="true"
                        className="my-1 border-t border-slate-100"
                      />
                    )}
                  </>
                )}
                {visibleNonFavoriteOptions.map((item) =>
                  renderOptionItem(item)
                )}
              </>
            )}
            {(isGrouped
              ? filteredOptionGroups.length === 0
              : filteredOptions.length === 0) && (
              <li className="px-4 py-3 text-sm text-gray-500">
                No options found
              </li>
            )}
            {hasMoreOptions && (
              <li className="sticky bottom-0 border-t border-slate-100 bg-white px-3 py-2">
                <button
                  type="button"
                  onClick={() =>
                    setVisibleOptionsCount(
                      (prev) => prev + INITIAL_VISIBLE_OPTIONS_COUNT
                    )
                  }
                  className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-700 hover:bg-slate-50"
                >
                  Show more ({visibleNonFavoriteOptions.length} of{' '}
                  {nonFavoriteFilteredOptions.length})
                </button>
              </li>
            )}
          </ul>,
          document.body
        )}
    </div>
  );
};

export default MultiSelectCheckBox;
